import type { CSSProperties, FC } from 'react'

import {
    ArrowUpRight,
    Heart,
    MessageCircle,
    Repeat2,
    Send
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { PageLink } from '@/components/kit/page-link'
import { Reveal } from '@/components/kit/reveal'
import { SplitText } from '@/components/kit/split-text'
import { START_HREF, START_LABEL } from '@/constants/site'

type Notification = {
    app: string
    time: string
    title: string
    body: string
}

interface FinalCtaProps {
    lines?: Array<string>
    italic?: string
    body?: string
    action?: string
    href?: string
    notifications?: Array<Notification>
}

const defaultNotifications: Array<Notification> = [
    { app: 'Instagram', time: 'now', title: 'Your reel passed 100K views', body: 'And it is still climbing. Someone call your mum.' },
    { app: 'TikTok', time: '2m', title: '@ninawearsthings commented', body: 'who made this?? i need it in my life' },
    { app: 'Messages', time: '6m', title: '312 people sent your post', body: 'That is the good kind of word of mouth.' },
    { app: 'Updraft', time: '11m', title: 'Next week’s forecast is ready', body: 'Three trends worth jumping on, one to skip.' }
]

const chips = [
    { icon: Heart, label: '4.2K', className: 'left-[4%] top-[12%]', delay: 0, rotate: -8 },
    { icon: MessageCircle, label: '318', className: 'left-[38%] bottom-[8%] hidden md:flex', delay: 2, rotate: 6 },
    { icon: Send, label: '1.1K', className: 'right-[3%] top-[6%]', delay: 1, rotate: 9 },
    { icon: Repeat2, label: '642', className: 'left-[46%] top-[6%] hidden lg:flex', delay: 3, rotate: -5 }
]

const AppGlyph: FC<{ app: string }> = ({ app }) => {
    const tone: Record<string, string> = {
        Instagram: 'bg-ink text-cream',
        TikTok: 'bg-ink text-ember',
        Messages: 'bg-[#2fbf5b] text-white',
        Updraft: 'bg-ember text-ink'
    }

    return (
        <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-[10px] font-heading text-sm font-bold', tone[app] ?? 'bg-ink text-cream')}>
            {app.charAt(0)}
        </span>
    )
}

const NotificationStack: FC<{ items: Array<Notification> }> = ({ items }) => {
    const { ref, inView } = useInView<HTMLDivElement>({ rootMargin: '0px 0px -20% 0px' })

    return (
        <div
            ref={ref}
            className='relative mx-auto w-full max-w-[400px]'
        >
            <div className='mb-5 text-center text-ink'>
                <p className='font-heading text-[4.25rem] font-bold leading-none tracking-[-0.04em]'>9:41</p>
                <p className='mt-1 font-mono text-xs uppercase tracking-[0.16em] text-ink/70'>Tuesday, the day it took off</p>
            </div>

            <ul className='flex flex-col gap-2.5'>
                {items.map((item, index) => (
                    <li
                        key={item.title}
                        className={cn(
                            'flex items-start gap-3 rounded-[1.25rem] border border-white/40 bg-paper/85 p-3.5 shadow-[0_18px_40px_-24px_rgb(30_30_30/0.55)] backdrop-blur-md',
                            inView ? 'pop-up' : 'opacity-0'
                        )}
                        style={{ animationDelay: `${200 + index * 420}ms` } as CSSProperties}
                    >
                        <AppGlyph app={item.app} />
                        <div className='min-w-0 flex-1'>
                            <div className='flex items-center justify-between gap-2'>
                                <p className='font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>{item.app}</p>
                                <p className='font-mono text-[10px] text-stone'>{item.time}</p>
                            </div>
                            <p className='mt-0.5 text-[14px] font-semibold leading-snug text-ink'>{item.title}</p>
                            <p className='text-[13px] leading-snug text-ink/70'>{item.body}</p>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    )
}

export const FinalCta: FC<FinalCtaProps> = ({
    lines = ['Your brand deserves', 'a bigger audience'],
    italic = 'bigger',
    body = 'Tell us where you are and where you want to be. You get a free forecast within 48 hours: what to post, where, and why it will work.',
    action = START_LABEL,
    href = START_HREF,
    notifications = defaultNotifications
}) => {
    return (
        <section
            id='start'
            aria-labelledby='final-cta-title'
            className='px-3 pt-24 md:px-5 md:pt-32'
        >
            <div className='grain reg-marks relative overflow-hidden rounded-[2rem] bg-ember px-6 py-16 [--grain-opacity:0.22] md:rounded-[3rem] md:px-14 md:py-24'>
                <div
                    aria-hidden='true'
                    className='pointer-events-none absolute inset-0'
                >
                    <div className='absolute inset-0 bg-[radial-gradient(rgb(30_30_30/0.16)_1px,transparent_1.4px)] [background-size:9px_9px] [mask-image:linear-gradient(115deg,transparent_45%,#000)]' />

                    {chips.map(chip => {
                        const Icon = chip.icon

                        return (
                            <span
                                key={chip.label}
                                className={cn('absolute flex animate-float items-center gap-1.5 rounded-full bg-ink px-3 py-1.5 font-mono text-xs text-cream shadow-[0_12px_24px_-12px_rgb(30_30_30/0.6)]', chip.className)}
                                style={{ rotate: `${chip.rotate}deg`, animationDelay: `${-chip.delay * 2}s`, animationDuration: `${8 + chip.delay}s` } as CSSProperties}
                            >
                                <Icon className='size-3.5 text-ember' />
                                {chip.label}
                            </span>
                        )
                    })}
                </div>

                <div className='relative z-10 grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]'>
                    <div>
                        <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-ink/75'>Incoming, 4 new notifications</p>

                        <SplitText
                            as='h2'
                            id='final-cta-title'
                            lines={lines}
                            italic={italic}
                            className='mt-5 text-[clamp(2.75rem,7vw,6.5rem)] font-bold leading-[0.9] tracking-[-0.045em] text-ink'
                        />

                        <Reveal
                            as='p'
                            delay={250}
                            className='mt-7 max-w-md text-lg leading-relaxed text-ink md:text-xl'
                        >
                            {body}
                        </Reveal>

                        <Reveal
                            delay={400}
                            className='mt-10 flex flex-wrap items-center gap-4'
                        >
                            <PageLink
                                href={href}
                                className='group inline-flex h-14 items-center gap-3 rounded-full bg-ink pl-7 pr-2 text-base font-medium text-cream transition-colors hover:bg-black'
                            >
                                {action}
                                <span className='flex size-10 items-center justify-center rounded-full bg-cream text-ink'>
                                    <ArrowUpRight className='size-4 transition-transform duration-500 ease-expo group-hover:rotate-45' />
                                </span>
                            </PageLink>

                            <span className='stamp -rotate-3 text-ink'>No contracts</span>
                        </Reveal>
                    </div>

                    <NotificationStack items={notifications} />
                </div>
            </div>
        </section>
    )
}
